import React from "react"
import { useSnackbar } from "notistack"
import { RoboComercial, RoboSuporte } from "../../../services/apiRobo"
import { erroMsg } from "../components/comum"

/**
 * Visão "Atendimentos" (rail Chats) — porte fiel da lista unificada da extensão:
 * chips macro (🎧 Suporte, 💼 Comercial, 🧑‍🤝‍🧑 Comunidade, 👨‍💻 Gestores, ❓ não classificadas),
 * células estilo WhatsApp Web e, ao clicar, a conversa (header + balões + notas + ações de ticket).
 * Usa as classes do CSS original (.suporte-group-card, .suporte-msg-in/out, .chat-note, etc.).
 */

type Macro = "suporte" | "comercial" | "comunidade" | "gestores" | "undefined"

interface Item {
    uid: string
    macro: Macro
    chatId: number | null
    name: string
    lastBody: string
    lastTs: any
    badge: string
    ticket?: any
    lead?: any
}

const CHIPS: Array<{ id: Macro; icon: string; title: string }> = [
    { id: "suporte", icon: "🎧", title: "Suporte" },
    { id: "comercial", icon: "💼", title: "Comercial" },
    { id: "comunidade", icon: "🧑‍🤝‍🧑", title: "Comunidade" },
    { id: "gestores", icon: "👨‍💻", title: "Gestores" },
    { id: "undefined", icon: "❓", title: "Não classificadas" },
]

export function Atendimentos() {
    const { enqueueSnackbar } = useSnackbar()
    const [ativos, setAtivos] = React.useState<Record<Macro, boolean>>({
        suporte: true,
        comercial: true,
        comunidade: true,
        gestores: true,
        undefined: true,
    })
    const [busca, setBusca] = React.useState("")
    const [itens, setItens] = React.useState<Item[]>([])
    const [carregando, setCarregando] = React.useState(false)
    const [aberto, setAberto] = React.useState<Item | null>(null)
    const [novoGrupo, setNovoGrupo] = React.useState(false)

    const carregar = React.useCallback(async () => {
        setCarregando(true)
        try {
            const [tickets, leadsResp, gestores] = await Promise.all([
                RoboSuporte.listarTickets({ limite: 300 }).catch(() => []),
                RoboComercial.listarLeads({ limite: 200 }).catch(() => []),
                RoboComercial.listarGestores().catch(() => []),
            ])
            const out: Item[] = []
            for (const t of arr(tickets)) {
                out.push({
                    uid: "t" + t.id,
                    macro: "suporte",
                    chatId: t.chatId,
                    name: t.nomeCliente || t.remetenteNome || t.remetenteWaId || "—",
                    lastBody: t.previaPrimeiraMensagem || "",
                    lastTs: t.ultimaMensagemEm || t.abertoEm,
                    badge: statusTicketBadge(t),
                    ticket: t,
                })
            }
            for (const l of arr(leadsResp.items || leadsResp)) {
                out.push({
                    uid: "l" + l.id,
                    macro: "comercial",
                    chatId: l.chatId,
                    name: l.nome || l.waId || l.telefone || "—",
                    lastBody: l.resumoIa || [l.cidade, l.uf].filter(Boolean).join("/") || "",
                    lastTs: l.ultimaMensagemEm,
                    badge: "💼 " + (l.estagio || "lead"),
                    lead: l,
                })
            }
            for (const g of arr(gestores)) {
                out.push({
                    uid: "g" + g.id,
                    macro: "gestores",
                    chatId: g.chatId || null,
                    name: g.nome || g.waId || "—",
                    lastBody: g.cidade || "",
                    lastTs: g.atualizadoEm,
                    badge: "👨‍💻 gestor",
                    lead: g,
                })
            }
            out.sort((a, b) => ts(b.lastTs) - ts(a.lastTs))
            setItens(out)
        } catch (e) {
            enqueueSnackbar(erroMsg(e), { variant: "error" })
        } finally {
            setCarregando(false)
        }
    }, [enqueueSnackbar])

    React.useEffect(() => {
        carregar()
    }, [carregar])

    const filtrados = itens.filter(i => {
        if (!ativos[i.macro]) return false
        if (busca.trim()) {
            const q = busca.trim().toLowerCase()
            return (i.name + " " + i.lastBody).toLowerCase().includes(q)
        }
        return true
    })

    if (aberto && aberto.macro === "gestores") {
        return (
            <GestorDetalhe
                lead={aberto.lead}
                aoVoltar={() => setAberto(null)}
                aoMudar={() => {
                    setAberto(null)
                    carregar()
                }}
            />
        )
    }
    if (aberto) {
        return (
            <ChatConversa
                item={aberto}
                aoVoltar={() => setAberto(null)}
                aoMudar={carregar}
            />
        )
    }

    return (
        <section className="panel active" style={{ display: "block" }}>
            <div id="suporte-list-view">
                <div className="atend-list-header">
                    <h2 className="atend-list-title">🎧 Atendimentos</h2>
                    <div className="atend-list-actions" style={{ display: "flex", gap: 6 }}>
                        <button
                            className="ghost"
                            title="Criar grupo de suporte na API oficial e gerar link de convite"
                            onClick={() => setNovoGrupo(true)}
                        >
                            ➕ Grupo de suporte
                        </button>
                        <button className="ghost-icon-btn" title="Atualizar" onClick={carregar}>
                            ↻
                        </button>
                    </div>
                </div>

                <div className="chats-macro-filter-bar">
                    {CHIPS.map(c => (
                        <div className="chats-macro-chip-wrap" key={c.id}>
                            <button
                                className={"chats-macro-chip" + (ativos[c.id] ? " active" : "")}
                                title={c.title}
                                onClick={() => setAtivos(a => ({ ...a, [c.id]: !a[c.id] }))}
                            >
                                {c.icon}
                            </button>
                        </div>
                    ))}
                </div>

                <div className="form-row" style={{ padding: "0 8px 8px" }}>
                    <input
                        type="text"
                        placeholder="🔎 filtrar por nome ou prévia"
                        value={busca}
                        onChange={e => setBusca(e.target.value)}
                    />
                </div>

                {carregando && <div className="muted small" style={{ padding: 16 }}>Carregando…</div>}
                {!carregando && filtrados.length === 0 && (
                    <div className="empty">Nenhuma conversa nos filtros atuais.</div>
                )}

                <ul className="list" style={{ listStyle: "none", margin: 0, padding: 0 }}>
                    {filtrados.map(i => (
                        <li key={i.uid}>
                            <Celula item={i} aoAbrir={() => setAberto(i)} />
                        </li>
                    ))}
                </ul>
            </div>
            {novoGrupo && (
                <CriarGrupoSuporte
                    aoFechar={() => setNovoGrupo(false)}
                    aoCriar={() => {
                        setNovoGrupo(false)
                        carregar()
                    }}
                />
            )}
        </section>
    )
}

function CriarGrupoSuporte({ aoFechar, aoCriar }: { aoFechar: () => void; aoCriar: () => void }) {
    const { enqueueSnackbar } = useSnackbar()
    const [nome, setNome] = React.useState("")
    const [cliente, setCliente] = React.useState("")
    const [descricao, setDescricao] = React.useState("")
    const [criando, setCriando] = React.useState(false)
    const [link, setLink] = React.useState<string | null>(null)

    async function criar() {
        if (!nome.trim()) {
            enqueueSnackbar("Informe o nome do grupo.", { variant: "warning" })
            return
        }
        setCriando(true)
        try {
            const r = await RoboSuporte.criarGrupo({
                nome: nome.trim(),
                descricao: descricao.trim() || undefined,
                nomeCliente: cliente.trim() || undefined,
            })
            setLink(r?.inviteLink || null)
            enqueueSnackbar(r?.inviteLink ? "Grupo criado!" : "Grupo criado (link indisponível agora).", {
                variant: "success",
            })
        } catch (e) {
            enqueueSnackbar(erroMsg(e), { variant: "error" })
        } finally {
            setCriando(false)
        }
    }

    async function copiar() {
        if (!link) return
        try {
            await navigator.clipboard.writeText(link)
            enqueueSnackbar("Link copiado.", { variant: "success" })
        } catch {
            enqueueSnackbar("Copie manualmente: " + link, { variant: "info" })
        }
    }

    return (
        <div className="modal" style={{ display: "flex" }}>
            <div className="modal-content" style={{ maxWidth: 520 }}>
                <div className="modal-header">
                    <strong>➕ Grupo de suporte (API oficial)</strong>
                    <button className="ghost-icon-btn" onClick={aoFechar}>
                        ✕
                    </button>
                </div>
                <div className="modal-body">
                    {!link ? (
                        <>
                            <div className="small muted" style={{ marginBottom: 8 }}>
                                Cria um grupo na API oficial da Meta e gera o link de convite. Mande o link no grupo
                                antigo para o cliente migrar. O suporte por IA passa a atender aqui.
                            </div>
                            <label className="small muted" style={{ display: "block", marginBottom: 8 }}>
                                Nome do grupo
                                <input type="text" value={nome} onChange={e => setNome(e.target.value)} style={{ width: "100%" }} />
                            </label>
                            <label className="small muted" style={{ display: "block", marginBottom: 8 }}>
                                Cliente (opcional)
                                <input type="text" value={cliente} onChange={e => setCliente(e.target.value)} style={{ width: "100%" }} />
                            </label>
                            <label className="small muted" style={{ display: "block" }}>
                                Descrição (opcional)
                                <textarea rows={2} value={descricao} onChange={e => setDescricao(e.target.value)} style={{ width: "100%" }} />
                            </label>
                        </>
                    ) : (
                        <>
                            <div className="small" style={{ marginBottom: 8 }}>
                                Grupo criado. Link de convite:
                            </div>
                            <div
                                style={{
                                    display: "flex",
                                    gap: 6,
                                    alignItems: "center",
                                    background: "var(--panel-2)",
                                    padding: 8,
                                    borderRadius: 6,
                                    wordBreak: "break-all",
                                }}
                            >
                                <a href={link} target="_blank" rel="noreferrer" style={{ flex: 1 }}>
                                    {link}
                                </a>
                                <button className="ghost" onClick={copiar}>
                                    Copiar
                                </button>
                            </div>
                        </>
                    )}
                </div>
                <div className="modal-footer">
                    {!link ? (
                        <>
                            <button className="ghost" onClick={aoFechar}>
                                Cancelar
                            </button>
                            <button disabled={criando || !nome.trim()} onClick={criar}>
                                {criando ? "Criando…" : "Criar grupo"}
                            </button>
                        </>
                    ) : (
                        <button onClick={aoCriar}>Concluir</button>
                    )}
                </div>
            </div>
        </div>
    )
}

function Celula({ item, aoAbrir }: { item: Item; aoAbrir: () => void }) {
    const hue = hueDoNome(item.name)
    return (
        <div
            className="suporte-group-card"
            style={{ cursor: "pointer" }}
            onClick={aoAbrir}
        >
            <div className="suporte-group-avatar" style={{ ["--avatar-hue" as any]: hue }}>
                <span className="suporte-group-avatar-fallback">{inicial(item.name)}</span>
            </div>
            <div className="suporte-group-card-body">
                <div className="suporte-group-name-row">
                    <span className="suporte-group-name" title={item.name}>
                        {item.name}
                    </span>
                    {item.lastTs ? (
                        <span className="suporte-group-time">{fmtRel(item.lastTs)}</span>
                    ) : null}
                </div>
                <div className="suporte-group-id">
                    <span className="suporte-group-status-badge">{item.badge}</span>
                </div>
                {item.lastBody ? (
                    <div className="suporte-group-last-msg">{item.lastBody.slice(0, 100)}</div>
                ) : null}
            </div>
        </div>
    )
}

function ChatConversa({
    item,
    aoVoltar,
    aoMudar,
}: {
    item: Item
    aoVoltar: () => void
    aoMudar: () => void
}) {
    const { enqueueSnackbar } = useSnackbar()
    const [msgs, setMsgs] = React.useState<any[]>([])
    const [notas, setNotas] = React.useState<any[]>([])
    const [contexto, setContexto] = React.useState<any>(null)
    const [carregando, setCarregando] = React.useState(false)
    const [novaNota, setNovaNota] = React.useState("")
    const [novaMsg, setNovaMsg] = React.useState("")
    const [enviando, setEnviando] = React.useState(false)
    const chatId = item.chatId

    const carregar = React.useCallback(async () => {
        if (!chatId) {
            setMsgs([])
            return
        }
        setCarregando(true)
        try {
            const [m, n, c] = await Promise.all([
                RoboSuporte.mensagens(chatId, { limite: 100 }),
                RoboSuporte.listarNotas(chatId).catch(() => []),
                item.macro === "suporte" ? RoboSuporte.contextoCliente(chatId).catch(() => null) : Promise.resolve(null),
            ])
            setMsgs(arr(m))
            setNotas(arr(n))
            setContexto(c)
        } catch (e) {
            enqueueSnackbar(erroMsg(e), { variant: "error" })
        } finally {
            setCarregando(false)
        }
    }, [chatId, item.macro, enqueueSnackbar])

    React.useEffect(() => {
        carregar()
    }, [carregar])

    async function acao(fn: () => Promise<any>, ok: string) {
        try {
            await fn()
            enqueueSnackbar(ok, { variant: "success" })
            aoMudar()
            carregar()
        } catch (e) {
            enqueueSnackbar(erroMsg(e), { variant: "error" })
        }
    }

    async function salvarNota() {
        if (!novaNota.trim() || !chatId) return
        try {
            await RoboSuporte.adicionarNota(chatId, novaNota.trim())
            setNovaNota("")
            setNotas(arr(await RoboSuporte.listarNotas(chatId)))
        } catch (e) {
            enqueueSnackbar(erroMsg(e), { variant: "error" })
        }
    }

    async function enviarMsg() {
        if (!novaMsg.trim() || !chatId) return
        setEnviando(true)
        try {
            await RoboSuporte.enviarMensagem(chatId, novaMsg.trim())
            setNovaMsg("")
            enqueueSnackbar("Mensagem enfileirada para envio.", { variant: "success" })
            setTimeout(carregar, 600)
        } catch (e) {
            enqueueSnackbar(erroMsg(e), { variant: "error" })
        } finally {
            setEnviando(false)
        }
    }

    const t = item.ticket
    const combinados = [
        ...msgs.map(m => ({ ...m, _kind: "msg" })),
        ...notas.map(n => ({ ...n, _kind: "note" })),
    ].sort((a, b) => ts(a.enviadaEm || a.criadoEm) - ts(b.enviadaEm || b.criadoEm))

    return (
        <section className="panel active" style={{ display: "block" }}>
            <div id="suporte-chat-view" style={{ display: "flex", flexDirection: "column", height: "100%" }}>
                <div className="suporte-chat-header">
                    <div className="suporte-chat-header-row1">
                        <button className="ghost" title="Voltar" onClick={aoVoltar}>
                            ←
                        </button>
                        <div className="suporte-group-avatar" style={{ ["--avatar-hue" as any]: hueDoNome(item.name) }}>
                            <span className="suporte-group-avatar-fallback">{inicial(item.name)}</span>
                        </div>
                        <strong className="suporte-chat-name-strong">{item.name}</strong>
                        <span className="suporte-group-status-badge" style={{ marginLeft: "auto" }}>
                            {item.badge}
                        </span>
                    </div>
                    {t && (
                        <div className="suporte-chat-header-stats" style={{ display: "flex", gap: 6, flexWrap: "wrap", padding: "6px 0" }}>
                            {t.status === "PENDENTE" && (
                                <button onClick={() => acao(() => RoboSuporte.pegarTicket(t.id), "Ticket assumido.")}>
                                    Assumir
                                </button>
                            )}
                            {t.status === "EM_ATENDIMENTO" && (
                                <button onClick={() => acao(() => RoboSuporte.fecharTicket(t.id, { followup: true }), "Ticket fechado.")}>
                                    Fechar
                                </button>
                            )}
                            {t.status === "FECHADO" && (
                                <button className="ghost" onClick={() => acao(() => RoboSuporte.reabrirTicket(t.id), "Ticket reaberto.")}>
                                    Reabrir
                                </button>
                            )}
                            <button className="ghost" onClick={() => acao(() => RoboSuporte.pausarIa(chatId!, { horas: 2 }), "IA pausada 2h.")}>
                                Pausar IA
                            </button>
                            <button className="ghost" onClick={() => acao(() => RoboSuporte.retomarIa(chatId!), "IA retomada.")}>
                                Retomar IA
                            </button>
                            {t.upsellEstado && t.upsellEstado !== "nenhum" && (
                                <span className="suporte-group-status-badge" title="Estado do upsell">
                                    🚀 upsell: {t.upsellModulo || t.upsellEstado}
                                </span>
                            )}
                        </div>
                    )}
                </div>

                {contexto && <ContextoCliente dados={contexto} />}

                <div className="suporte-chat-messages" id="suporte-chat-messages" style={{ flex: 1, overflowY: "auto" }}>
                    {carregando && <div className="muted small" style={{ textAlign: "center", padding: 20 }}>Carregando…</div>}
                    {!carregando && combinados.length === 0 && (
                        <div className="muted small" style={{ textAlign: "center", padding: 20 }}>
                            (sem mensagens armazenadas)
                        </div>
                    )}
                    {combinados.map((m, i) =>
                        m._kind === "note" ? (
                            <div className="chat-note" key={"n" + (m.id || i)}>
                                <div className="chat-note-header">
                                    <span className="chat-note-icon">📝</span>
                                    <span className="chat-note-label">Nota interna (só a equipe vê)</span>
                                    <span className="chat-note-time">{fmtData(m.criadoEm)}</span>
                                </div>
                                <div className="chat-note-body">{m.corpo}</div>
                            </div>
                        ) : (
                            <div className={m.fromMe ? "suporte-msg-out" : "suporte-msg-in"} key={"m" + (m.id || i)}>
                                {!m.fromMe && (m.remetenteNome || m.remetenteWaId) ? (
                                    <div className="suporte-msg-sender">{m.remetenteNome || m.remetenteWaId}</div>
                                ) : null}
                                <div className="suporte-msg-body">{m.corpo || m.corpoOriginal || "—"}</div>
                                <div className="suporte-msg-time">
                                    {fmtData(m.enviadaEm)} {m.autoGerada ? "· IA" : m.fromMe ? "· equipe" : ""}
                                </div>
                            </div>
                        )
                    )}
                </div>

                <div className="suporte-chat-composer" style={{ padding: 8, borderTop: "1px solid var(--border)" }}>
                    <div className="form-row" style={{ gap: 6 }}>
                        <textarea
                            rows={2}
                            placeholder="Mensagem para o cliente…"
                            value={novaMsg}
                            onChange={e => setNovaMsg(e.target.value)}
                            onKeyDown={e => {
                                if (e.key === "Enter" && !e.shiftKey) {
                                    e.preventDefault()
                                    enviarMsg()
                                }
                            }}
                            style={{ flex: 1, resize: "vertical" }}
                        />
                        <button onClick={enviarMsg} disabled={!novaMsg.trim() || enviando || !chatId}>
                            {enviando ? "…" : "Enviar"}
                        </button>
                    </div>
                    <div className="form-row" style={{ gap: 6, marginTop: 6 }}>
                        <input
                            type="text"
                            placeholder="Nota interna (só a equipe vê)…"
                            value={novaNota}
                            onChange={e => setNovaNota(e.target.value)}
                            onKeyDown={e => {
                                if (e.key === "Enter") salvarNota()
                            }}
                        />
                        <button className="ghost" onClick={salvarNota} disabled={!novaNota.trim()}>
                            Anotar
                        </button>
                    </div>
                    {!chatId && (
                        <div className="muted small" style={{ marginTop: 4 }}>
                            Este item ainda não tem conversa vinculada.
                        </div>
                    )}
                </div>
            </div>
        </section>
    )
}

function GestorDetalhe({ lead, aoVoltar, aoMudar }: { lead: any; aoVoltar: () => void; aoMudar: () => void }) {
    const { enqueueSnackbar } = useSnackbar()
    const [servicos, setServicos] = React.useState(lead?.gestorServicos || lead?.servicos || "")
    const [notas, setNotas] = React.useState(lead?.gestorNotas || lead?.notas || "")
    const [parceiro, setParceiro] = React.useState(!!(lead?.gestorIsParceiro || lead?.isParceiro))
    const [salvando, setSalvando] = React.useState(false)

    async function salvar() {
        setSalvando(true)
        try {
            await RoboComercial.salvarGestor(lead.id, { servicos, notas, isParceiro: parceiro })
            enqueueSnackbar("Gestor salvo.", { variant: "success" })
            aoMudar()
        } catch (e) {
            enqueueSnackbar(erroMsg(e), { variant: "error" })
        } finally {
            setSalvando(false)
        }
    }

    return (
        <section className="panel active" style={{ display: "block" }}>
            <div className="suporte-chat-header">
                <div className="suporte-chat-header-row1">
                    <button className="ghost" title="Voltar" onClick={aoVoltar}>
                        ←
                    </button>
                    <strong className="suporte-chat-name-strong">👨‍💻 {lead?.nome || lead?.waId || "Gestor"}</strong>
                </div>
            </div>
            <div style={{ padding: 12, maxWidth: 560 }}>
                <div className="small muted" style={{ marginBottom: 8 }}>
                    {[lead?.cidade, lead?.uf].filter(Boolean).join("/") || "sem cidade"}
                    {lead?.telefone ? " · " + lead.telefone : ""}
                </div>
                <label className="small muted" style={{ display: "block", marginBottom: 8 }}>
                    Serviços que oferece
                    <textarea rows={2} value={servicos} onChange={e => setServicos(e.target.value)} style={{ width: "100%" }} />
                </label>
                <label className="small muted" style={{ display: "block", marginBottom: 8 }}>
                    Notas internas
                    <textarea rows={3} value={notas} onChange={e => setNotas(e.target.value)} style={{ width: "100%" }} />
                </label>
                <label className="small" style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12 }}>
                    <input type="checkbox" checked={parceiro} onChange={e => setParceiro(e.target.checked)} />
                    <span>É parceiro</span>
                </label>
                <button disabled={salvando} onClick={salvar}>
                    {salvando ? "Salvando…" : "💾 Salvar gestor"}
                </button>
            </div>
        </section>
    )
}

function ContextoCliente({ dados }: { dados: any }) {
    // o endpoint devolve uma LISTA de estabelecimentos do cliente
    const lista: any[] = Array.isArray(dados) ? dados : dados?.estabelecimentos || (dados ? [dados] : [])
    if (lista.length === 0) {
        return (
            <div className="suporte-client-card" style={{ padding: "8px 12px", background: "var(--panel-2)", borderBottom: "1px solid var(--border)" }}>
                <span className="muted small">Sem estabelecimento vinculado.</span>
            </div>
        )
    }
    return (
        <div className="suporte-client-card" style={{ padding: "8px 12px", background: "var(--panel-2)", borderBottom: "1px solid var(--border)", maxHeight: 180, overflowY: "auto" }}>
            {lista.map((e, i) => {
                const a = e.assinatura || {}
                const faturas = typeof e.faturasEmAberto === "number" ? e.faturasEmAberto : (Array.isArray(e.itens) ? e.itens.length : e.faturasEmAberto)
                const mods = [
                    e.apiOficial ? "API oficial" : null,
                    e.modFiscal ? "Fiscal" : null,
                    e.ifood ? "iFood" : null,
                    e.pixOnline ? "PIX online" : null,
                ].filter(Boolean)
                return (
                    <div key={e.id || i} style={{ marginBottom: i < lista.length - 1 ? 8 : 0 }}>
                        <div className="small">
                            <strong>{e.nome || "Estabelecimento"}</strong>
                            {e.cidade || e.uf ? <span className="muted"> · {[e.cidade, e.uf].filter(Boolean).join("/")}</span> : null}
                        </div>
                        {(a.status || a.valor != null) && (
                            <div className="small">
                                Assinatura: <strong>{a.status || (a.ativo ? "ativa" : "—")}</strong>
                                {a.valor != null ? " · R$ " + a.valor : ""}
                                {a.proximoVencimento || a.dataVencimento ? " · vence " + fmtDataCurta(a.proximoVencimento || a.dataVencimento) : ""}
                            </div>
                        )}
                        <div className="small">
                            Faturas em aberto: <strong style={{ color: faturas ? "var(--sp)" : "inherit" }}>{faturas ?? 0}</strong>
                        </div>
                        {mods.length > 0 && <div className="small muted">Módulos: {mods.join(", ")}</div>}
                    </div>
                )
            })}
        </div>
    )
}

function fmtDataCurta(v: any): string {
    if (!v) return ""
    try {
        return new Date(v).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit" })
    } catch {
        return String(v)
    }
}

// ─── utils ──────────────────────────────────────────────────────────────────
function arr(v: any): any[] {
    return Array.isArray(v) ? v : []
}
function ts(v: any): number {
    if (!v) return 0
    const n = typeof v === "number" ? v : Date.parse(v)
    return isNaN(n) ? 0 : n
}
function fmtData(v: any): string {
    if (!v) return ""
    try {
        return new Date(v).toLocaleString("pt-BR", {
            day: "2-digit",
            month: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
        })
    } catch {
        return ""
    }
}
function fmtRel(v: any): string {
    const d = ts(v)
    if (!d) return ""
    const diff = Date.now() - d
    const min = Math.floor(diff / 60000)
    if (min < 1) return "agora"
    if (min < 60) return min + "m"
    const h = Math.floor(min / 60)
    if (h < 24) return h + "h"
    return fmtData(v)
}
function inicial(nome: string): string {
    const s = (nome || "?").trim()
    return s ? s[0].toUpperCase() : "?"
}
function hueDoNome(nome: string): number {
    let h = 0
    for (let i = 0; i < (nome || "").length; i++) h = (h * 31 + nome.charCodeAt(i)) % 360
    return h
}

function statusTicketBadge(t: any): string {
    if (t.humanoSolicitado) return "🙋 pediu humano"
    if (t.status === "PENDENTE") return "🎫 pendente"
    if (t.status === "EM_ATENDIMENTO") return "🎧 em atendimento"
    if (t.status === "FECHADO") return "✅ fechado"
    return t.status || ""
}
